/**
 * Recovery from stale-build chunk failures.
 *
 * THE PROBLEM
 *   PesaFii is served from Vercel on the Hobby plan. Vercel deployments are
 *   immutable and atomic — but the production alias points at exactly ONE
 *   deployment. A lazy chunk's hashed filename only exists inside the build that
 *   produced it, so once a new deployment goes live:
 *
 *     GET /static/js/5003.5838ec39.chunk.js  ->  404 (correct, honest)
 *     React                                  ->  ChunkLoadError
 *     error boundary                         ->  "Something went wrong"
 *
 *   An admin who left a tab open across a deploy hits this on their next lazy
 *   route. Vercel's Skew Protection solves it at the platform level by pinning a
 *   client to its original deployment, but that is Pro+ — so this module is the
 *   Hobby-tier equivalent.
 *
 * WHAT THIS CAN AND CANNOT DO
 *   It CANNOT stop the 404 — the file genuinely is not there any more.
 *   It CAN make the failure invisible: detect it, reload ONCE to pick up the new
 *   build, and if that does not work, show an honest "update available" screen
 *   instead of a broken shell.
 *
 * WHY ONE RELOAD, SCOPED TO THE BUILD
 *   A reload loop is worse than the bug it fixes — it would make the app
 *   permanently unusable and burn a captive-portal customer's data. The marker
 *   is therefore keyed to the BUILD ID: one automatic reload per build. If the
 *   fresh build still fails, that is a real bug, not staleness, and we stop and
 *   say so rather than reloading again.
 */

/** Identifies the running build. Set at build time; falls back for local dev. */
export const BUILD_ID =
  process.env.REACT_APP_BUILD_ID ||
  process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA ||
  "dev";

const MARKER_KEY = "pf_chunk_recovery_v1";

/**
 * Is this the "my code is stale" failure, or a genuine application bug?
 *
 * Deliberately narrow. Reloading on a generic error would hide real defects and
 * turn a visible bug into a mysterious refresh, so only the known stale-asset
 * shapes qualify.
 */
export function isChunkLoadError(error) {
  if (!error) return false;

  // webpack sets error.name = "ChunkLoadError"; some browsers only give a string.
  const name = String(error.name || "");
  if (name === "ChunkLoadError") return true;

  const message = String(error.message || error.reason?.message || error || "");
  return (
    /Loading chunk [\w-]+ failed/i.test(message) ||        // webpack 4/5
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||    // mini-css-extract
    /Failed to fetch dynamically imported module/i.test(message) || // vite/native
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)      // Safari
  );
}

/** Recovery state for THIS build. A different build starts fresh by design. */
function readMarker() {
  try {
    const raw = sessionStorage.getItem(MARKER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.buildId ? parsed : null;
  } catch (_) {
    return null; // private mode / storage disabled — treated as "never tried"
  }
}

/** True when this exact build has already had its one automatic reload. */
export function hasAlreadyRecovered() {
  const m = readMarker();
  return !!m && m.buildId === BUILD_ID;
}

/**
 * Clear the marker once the app has booted successfully.
 *
 * Without this, a customer who hit a chunk error at 09:00 would still be marked
 * hours later, and a genuine second staleness event on the same build could not
 * self-heal. Called from App on mount.
 */
export function clearRecoveryMarker() {
  try {
    const m = readMarker();
    if (m && m.buildId === BUILD_ID) sessionStorage.removeItem(MARKER_KEY);
  } catch (_) { /* nothing to clear */ }
}

/**
 * Attempt the one automatic recovery.
 *
 * @returns {boolean} true if a reload was started (caller should render nothing
 *          further); false if recovery is exhausted and the caller must show the
 *          update screen.
 */
export function attemptChunkRecovery(details = {}) {
  if (hasAlreadyRecovered()) return false;

  try {
    sessionStorage.setItem(
      MARKER_KEY,
      JSON.stringify({ buildId: BUILD_ID, at: Date.now(), ...details })
    );
  } catch (_) {
    // Storage unavailable means the marker cannot bound the retry, and an
    // unbounded reload loop is far worse than one manual refresh. Refuse.
    return false;
  }

  // reload(true) is non-standard and ignored by modern browsers. A plain reload
  // is enough: index.html is served must-revalidate, so the browser revalidates
  // the shell and picks up the new asset URLs. No cache-busting query parameter
  // is appended — those accumulate in history and in shared links.
  try {
    window.location.reload();
  } catch (_) {
    return false;
  }
  return true;
}

/** Safe diagnostic context. Carries no token, password or financial data. */
export function chunkErrorContext(error) {
  let swActive = false;
  try {
    swActive = !!(navigator.serviceWorker && navigator.serviceWorker.controller);
  } catch (_) { /* not supported */ }

  return {
    build_id: BUILD_ID,
    chunk_error: true,
    requested_url: String(error?.request || error?.filename || ""),
    route: typeof window !== "undefined" ? window.location.pathname : "",
    recovery_attempted: hasAlreadyRecovered(),
    service_worker_active: swActive,
    online: typeof navigator !== "undefined" ? navigator.onLine !== false : true,
  };
}
